import { useEffect, useRef, useState } from "react";

/**
 * Seconds left on the current question, counted down locally between
 * polls. The server's `secondsLeft` is the truth (D8): each NEW value
 * re-anchors the local clock, unless it agrees within 400 ms (so the
 * number doesn't jitter). Re-renders alone never re-anchor: an earlier
 * version did, every 100 ms, to the stale value, so the bar sawed back
 * and froze when polls slowed (review of #451). `key` (the question
 * index) restarts it for a new question. Returns null when there's no
 * countdown.
 */
export default function useCountdown(secondsLeft, key) {
  const endsAt = useRef(null);
  const anchoredFor = useRef(null);
  const [left, setLeft] = useState(secondsLeft ?? null);
  const counting = secondsLeft !== null && secondsLeft !== undefined;

  useEffect(() => {
    if (!counting) {
      endsAt.current = null;
      setLeft(null);
      return undefined;
    }
    const target = Date.now() + secondsLeft * 1000;
    if (
      endsAt.current === null ||
      anchoredFor.current !== key ||
      Math.abs(target - endsAt.current) > 400
    ) {
      endsAt.current = target;
      anchoredFor.current = key;
    }
    const tick = () => setLeft(Math.max(0, (endsAt.current - Date.now()) / 1000));
    tick();
    const timer = setInterval(tick, 100);
    return () => clearInterval(timer);
  }, [counting, secondsLeft, key]);

  return counting ? left : null;
}
